import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { INVALID_TOPIC_CHARACTERS_REGEX } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetupConst";
import { LLMSetupSectionHeader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupSectionHeader";
import { MAX_TOPIC_NAME_LENGTH } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";

interface ILLMSetupTopicsProps {
    selectedTopics: string[];
    canTopicsBeAdded: boolean;
    onTopicAdded: (topic: string) => void;
    onTopicRemove: (topic: string) => void;
    error?: string;
    onUserInteraction?: () => void;
}

export const AddedTopicsSection: FC<ILLMSetupTopicsProps> = ({
    selectedTopics,
    canTopicsBeAdded,
    onTopicAdded,
    onTopicRemove,
    error: externalError,
    onUserInteraction,
}) => {
    const [topicToAdd, setTopicToAdd] = useState<string>("");
    const [fieldError, setFieldError] = useState<string>("");
    const selectedTopicsRef = useRef<string[]>(selectedTopics);
    const topicFieldRef = useRef<Textfield>(null);

    const error = fieldError || externalError || "";

    useEffect(() => {
        selectedTopicsRef.current = selectedTopics;
    }, [selectedTopics]);

    const onTopicAdd = (): void => {
        if (!topicToAdd) {
            return;
        }

        onTopicAdded(topicToAdd);
        setTopicToAdd("");
    };

    const onTopicFieldChange = (value: string): void => {
        const trimmedValue = value.trim();
        onUserInteraction?.();

        if (trimmedValue.includes(",")) {
            setFieldError(translate("llm.setup.modal.topics.error.comma"));
            return;
        }

        if (INVALID_TOPIC_CHARACTERS_REGEX.test(trimmedValue)) {
            setFieldError(translate("llm.setup.modal.topics.error.invalid_characters"));
            return;
        }

        const isDuplicate = selectedTopicsRef.current.some(
            (topic) => topic.toLowerCase() === trimmedValue.toLowerCase(),
        );

        if (isDuplicate) {
            setFieldError(translate("llm.setup.modal.topics.error.duplicate"));
            return;
        }

        setTopicToAdd(trimmedValue);
        setFieldError("");
    };

    const onTopicFieldKeyDown = (ev: KeyboardEvent): void => {
        if (ev.key === "Enter" && !error) {
            onTopicAdd();
            topicFieldRef?.current?.clearValue?.();
        }
    };

    return (
        <>
            <LLMSetupSectionHeader
                title={
                    <Text
                        fontVariant="subtitle2"
                        data-automation="llm-setup-topics-title"
                    >
                        {translate("llm.setup.topics.title")}
                    </Text>
                }
            />
            <Textfield
                ref={topicFieldRef}
                data-automation="llm-setup-brand-name-input"
                placeholder={translate("llm.setup.modal.topics.placeholder")}
                ApplyButtonText={translate("global.add")}
                onChange={onTopicFieldChange}
                onApplyClick={fieldError ? undefined : onTopicAdd}
                error={!!error}
                errorMessage={error}
                disabled={!canTopicsBeAdded}
                onKeyDown={onTopicFieldKeyDown}
                maxLength={MAX_TOPIC_NAME_LENGTH}
            />
            {selectedTopics.length > 0 ? (
                <Flex
                    flexWrap="wrap"
                    overflow="auto"
                >
                    {selectedTopics.map((topic) => (
                        <SimpleChipItem
                            key={topic}
                            text={topic}
                            onCloseItem={() => onTopicRemove(topic)}
                        />
                    ))}
                </Flex>
            ) : null}
        </>
    );
};
